import { median } from '../lib/math';
import { err, ok, type Result } from '../lib/result';
import { laughScore } from './scoring';
import type { FaceFrame } from './types';

export interface Calibration {
  /** Score de rire médian au repos. */
  readonly baseline: number;
  /** Dispersion robuste au repos (MAD normalisé). */
  readonly noise: number;
  /** Au-dessus : début de rire possible. */
  readonly threshold: number;
  /** En dessous : fin de rire (hystérésis). */
  readonly release: number;
}

export type CalibrationError =
  | 'notEnoughSamples'
  | 'noFace'
  | 'notSerious'
  /** Analyse trop lente pour ce mode (clignement). */
  | 'tooSlow';

export interface CalibrationOptions {
  readonly minSamples: number;
  /** Proportion minimale de frames avec visage. */
  readonly minFaceRatio: number;
  /** Au-delà, le joueur ne fait pas « son air sérieux ». */
  readonly maxBaseline: number;
  readonly minMargin: number;
  readonly noiseFactor: number;
  readonly maxThreshold: number;
}

export const DEFAULT_CALIBRATION_OPTIONS: CalibrationOptions = {
  minSamples: 15,
  minFaceRatio: 0.8,
  maxBaseline: 0.3,
  minMargin: 0.25,
  noiseFactor: 4,
  maxThreshold: 0.9,
};

/** Scores au repos (null = pas de visage sur la frame) → baseline + seuils personnels. */
export function calibrate(
  scores: readonly (number | null)[],
  options: Partial<CalibrationOptions> = {},
): Result<Calibration, CalibrationError> {
  const o = { ...DEFAULT_CALIBRATION_OPTIONS, ...options };
  if (scores.length < o.minSamples) return err('notEnoughSamples');
  const faces = scores.filter((s): s is number => s !== null);
  if (faces.length / scores.length < o.minFaceRatio) return err('noFace');
  if (faces.length < o.minSamples) return err('notEnoughSamples');

  const baseline = median(faces);
  if (baseline > o.maxBaseline) return err('notSerious');
  const noise = 1.4826 * median(faces.map((s) => Math.abs(s - baseline)));
  const threshold = Math.min(
    o.maxThreshold,
    baseline + Math.max(o.minMargin, o.noiseFactor * noise),
  );
  const release = baseline + (threshold - baseline) / 2;
  return ok({ baseline, noise, threshold, release });
}

export function calibrateFromFrames(
  frames: readonly FaceFrame[],
  options: Partial<CalibrationOptions> = {},
): Result<Calibration, CalibrationError> {
  return calibrate(
    frames.map((f) => (f.blendshapes ? laughScore(f.blendshapes) : null)),
    options,
  );
}
